/** SUNTRONIX ONE — Customer Contact Reconciliation (spec §30).
 *  Read-only hygiene report over crm_contacts: which customers have one vs
 *  many contacts, and which contacts are unnamed / duplicated / missing a
 *  role / email / phone. Remediation happens in Customer 360 via the normal
 *  edit / deactivate / set-primary actions. */
import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { RefreshCw, Users, AlertTriangle, ArrowLeft } from "lucide-react";

const Stat = ({ label, value, tone = "slate", testid }) => (
  <div className={`border rounded-lg p-3 bg-white border-slate-200`} data-testid={testid}>
    <div className={`text-2xl font-semibold text-${tone}-800`}>{value}</div>
    <div className="text-[11px] uppercase tracking-wide text-slate-500 mt-0.5">{label}</div>
  </div>
);

function IssueList({ title, rows, nav, keyName = "name" }) {
  if (!rows || rows.length === 0) return null;
  return (
    <section className="border border-slate-200 rounded-lg bg-white p-4" data-testid={`recon-list-${title.replace(/\s+/g, "-").toLowerCase()}`}>
      <div className="text-[13px] font-semibold text-slate-800 mb-2">{title} <span className="text-slate-400">({rows.length})</span></div>
      <div className="divide-y divide-slate-100 max-h-72 overflow-y-auto">
        {rows.map((r, i) => (
          <button key={r.id || i} onClick={() => r.customer_id && nav(`/commercial/crm/customers/${r.customer_id}`)}
            className="w-full text-left py-2 flex items-center justify-between hover:bg-slate-50 px-1 rounded"
            data-testid={`recon-row-${r.id || i}`}>
            <span className="text-[12px] text-slate-800">{r[keyName] || r.email || "—"}</span>
            <span className="text-[11px] text-slate-500 truncate max-w-[240px]">{[r.email, r.phone].filter(Boolean).join(" · ")}</span>
          </button>
        ))}
      </div>
    </section>
  );
}

export default function ContactReconcile() {
  const nav = useNavigate();
  const [data, setData] = useState(null);
  const [busy, setBusy] = useState(false);

  const run = async () => {
    setBusy(true);
    try {
      const { data } = await api.get("/vtiger/crm-contacts/reconcile-report");
      setData(data);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to run scan");
    } finally { setBusy(false); }
  };

  useEffect(() => { run(); }, []);

  const s = data?.summary || {};
  return (
    <div className="max-w-[1400px] mx-auto p-6 space-y-6" data-testid="contact-reconcile-page">
      <div className="flex items-center gap-3">
        <button onClick={() => nav(-1)} className="text-slate-500 hover:text-slate-900" data-testid="recon-back"><ArrowLeft className="h-4 w-4" /></button>
        <Users className="h-5 w-5 text-emerald-700" />
        <div>
          <h1 className="text-lg font-semibold text-slate-900">Contact Reconciliation</h1>
          <p className="text-[12px] text-slate-500">Find customers with missing, duplicate or incomplete contacts. Click any row to clean it up in Customer 360.</p>
        </div>
        <Button onClick={run} disabled={busy} className="ml-auto bg-emerald-700 hover:bg-emerald-800 text-white" data-testid="recon-run">
          <RefreshCw className={`h-4 w-4 mr-1.5 ${busy ? "animate-spin" : ""}`} /> {busy ? "Scanning…" : "Re-run scan"}
        </Button>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-3">
        <Stat label="Customers scanned" value={s.customers_scanned ?? "—"} testid="stat-customers" />
        <Stat label="Contacts total" value={s.contacts_total ?? "—"} testid="stat-contacts" />
        <Stat label="Single contact" value={s.customers_single_contact ?? "—"} testid="stat-single" />
        <Stat label="Multiple contacts" value={s.customers_multiple_contacts ?? "—"} tone="emerald" testid="stat-multiple" />
        <Stat label="Unnamed" value={s.unnamed_contacts ?? "—"} tone="amber" testid="stat-unnamed" />
        <Stat label="Duplicate emails" value={s.duplicate_email_groups ?? "—"} tone="rose" testid="stat-dupes" />
        <Stat label="No role" value={s.contacts_without_role ?? "—"} tone="amber" testid="stat-norole" />
        <Stat label="No email" value={s.contacts_without_email ?? "—"} tone="amber" testid="stat-noemail" />
        <Stat label="No phone" value={s.contacts_without_phone ?? "—"} tone="amber" testid="stat-nophone" />
      </div>

      {data && s.unnamed_contacts === 0 && s.contacts_without_role === 0 &&
        s.contacts_without_email === 0 && s.contacts_without_phone === 0 &&
        s.duplicate_email_groups === 0 && (
        <div className="border border-emerald-200 bg-emerald-50 rounded-lg p-4 text-[13px] text-emerald-800 flex items-center gap-2" data-testid="recon-clean">
          <AlertTriangle className="h-4 w-4" /> No contact hygiene issues found. Every contact has a name, role, email and phone.
        </div>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <IssueList title="Unnamed contacts" rows={data?.unnamed} nav={nav} />
        <IssueList title="Contacts without a role" rows={data?.without_role} nav={nav} />
        <IssueList title="Contacts without email" rows={data?.without_email} nav={nav} />
        <IssueList title="Contacts without phone" rows={data?.without_phone} nav={nav} />
      </div>

      {data?.duplicates?.length > 0 && (
        <section className="border border-rose-200 rounded-lg bg-white p-4" data-testid="recon-duplicates">
          <div className="text-[13px] font-semibold text-rose-800 mb-2">Duplicate emails across customers ({data.duplicates.length})</div>
          <div className="space-y-3 max-h-96 overflow-y-auto">
            {data.duplicates.map((g, i) => (
              <div key={i} className="border border-slate-100 rounded-md p-2" data-testid={`recon-dupe-${i}`}>
                <div className="text-[12px] font-mono text-slate-700">{g.email}</div>
                <div className="mt-1 space-y-1">
                  {g.contacts.map((c) => (
                    <button key={c.id} onClick={() => nav(`/commercial/crm/customers/${c.customer_id}`)}
                      className="w-full text-left text-[11px] text-slate-600 hover:underline">
                      {c.name} · {c.phone || "no phone"}
                    </button>
                  ))}
                </div>
              </div>
            ))}
          </div>
        </section>
      )}
    </div>
  );
}
